import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { tagText } from '../lib/assets';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useConfirm } from '../components/ConfirmDialog';
import '../components/Assets.css';
import BackLink from '../components/asset/BackLink';
import { cameFrom } from '../components/asset/BackLink';

/**
 * Linked automatically: the devices the register made without a click.
 *
 * When "Link plugged-in devices to the computer at once" is on, a laptop's
 * report turns each screen, dock, keyboard, mouse, webcam and headset it
 * sees into a device, plugged into that laptop, with its holder and place.
 * For a week that also made devices of things that were not the firm's or
 * not things at all - a headset paired once at home, a home printer, every
 * laptop's own camera - so this page lists everything that was linked that
 * way, newest first, for IT to look over once:
 *
 *   Delete (junk)   the device is deleted - gone, not retired - and its
 *                   inbox row goes to the Ignored list, so the next report
 *                   does not make it again. Only a device nothing else has
 *                   ever been written about (no history of its own, no
 *                   ticket, no document) can be deleted; the server refuses
 *                   the rest by name, and the page says so.
 *   Mark personal   NOT deleted (105): the device becomes the holder's
 *                   personal item - no tag, out of the company list, still
 *                   theirs on My equipment - and is never linked as the
 *                   firm's again, on their next laptop either.
 *   Keep            looked at, gets its asset tag, and leaves this list.
 *
 * Each row names the laptop that reported it, who held it, and where the
 * laptop's report placed it (office, home, client) at the time - the one
 * thing that most often says whether it is the firm's.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const WHERE = { office: ['In the office', 'success'], away: ['At home', 'warning'], client: ['At a client', 'info'], unknown: ['Not known', 'idle'] };
const SINCE = [['7', 'Last 7 days'], ['30', 'Last 30 days'], ['90', 'Last 90 days'], ['', 'All time']];
const BUSES = { usb: 'USB', display: 'Display', thunderbolt: 'Thunderbolt', bluetooth: 'Bluetooth', network: 'Network', unknown: 'Unknown' };

const when = (t) => (t ? new Date(t).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '');
const name = (d) => [d.manufacturer, d.model].filter(Boolean).join(' ') || tagText(d);

export default function AssetDiscoveryCleanup() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');        // from an action: stays until the next action
  const [loadError, setLoadError] = useState(''); // from loading the list
  const [note, setNote] = useState('');
  const [since, setSince] = useState('30');
  const [kind, setKind] = useState('');
  const [bus, setBus] = useState('');
  const [where, setWhere] = useState('');
  const [holder, setHolder] = useState('');      // '' everybody's, 'me' my own
  const [rulesOnly, setRulesOnly] = useState(false);
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [confirm, confirmDialog] = useConfirm();

  const load = useCallback(() => {
    const q = new URLSearchParams();
    if (since) q.set('since', new Date(Date.now() - Number(since) * 86400000).toISOString());
    if (kind) q.set('kind', kind);
    if (bus) q.set('bus', bus);
    if (where) q.set('where', where);
    if (holder) q.set('holder', holder);
    axios.get(`${API_BASE}/assets/discoveries/autolinked?${q}`)
      .then((r) => { setData(r.data); setLoadError(''); })
      .catch((e) => setLoadError(e.response?.data?.error || 'Could not load the list.'));
  }, [since, kind, bus, where, holder]);
  useEffect(() => { load(); }, [load]);

  // "Only what today's rules would not add": software printers, built-in parts.
  const devices = useMemo(() => (data?.devices || []).filter((d) => !rulesOnly || d.rules_say), [data, rulesOnly]);
  const kinds = data?.kinds || {};
  const chosen = useMemo(() => devices.filter((d) => picked.has(d.asset_id)), [devices, picked]);
  const deletable = chosen.filter((d) => !d.has_history);
  // Sent back to the inbox: anything but a device with a ticket or a document.
  const sendable = chosen.filter((d) => !(d.tickets > 0 || d.documents > 0));
  const toggle = (id) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const holders = [...new Set(chosen.map((d) => d.holder_name).filter(Boolean))];

  const run = async (action, ids) => {
    setBusy(true);
    setError('');
    setNote('');
    try {
      const r = await axios.post(`${API_BASE}/assets/discoveries/cleanup`, { ids, action });
      const { done = [], refused = [] } = r.data || {};
      const verb = action === 'keep' ? 'kept' : action === 'personal' ? 'marked as a personal item'
        : action === 'inbox' ? 'sent back to the Discovery inbox to decide again' : 'deleted';
      if (done.length) setNote(`${done.length} device${done.length === 1 ? '' : 's'} ${verb}.`);
      if (refused.length) setError(refused.map((x) => x.reason).join(' · '));
      setPicked(new Set());
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'That did not work.');
    } finally { setBusy(false); }
  };

  const askDelete = async (personal) => {
    const rows = personal ? chosen : deletable;
    const ids = rows.map((d) => d.asset_id);
    const kept = personal ? 0 : chosen.length - deletable.length;
    const title = personal
      ? `Mark ${ids.length} device${ids.length === 1 ? '' : 's'} as personal?`
      : `Delete ${ids.length} device${ids.length === 1 ? '' : 's'} from the register?`;
    const ok = await confirm({
      title, confirmLabel: personal ? 'Mark as personal' : 'Delete', danger: !personal,
      message: (
        <>
          <p>
            {personal
              ? `Each device becomes ${holders.length === 1 ? `${holders[0]}’s` : 'its holder’s'} personal item: it loses its tag and leaves the company list, and stays with them under My equipment. The same thing seen with ${holders.length === 1 ? 'their' : 'that person’s'} next laptop is never linked as the firm’s again.`
              : 'Each device is deleted outright - not retired - with the lines of history its linking wrote. Its inbox row stays on the Ignored list, so the next report does not make it again.'}
          </p>
          <ul className="as-tree" data-testid="cleanup-changes">
            {rows.map((d) => <li key={d.asset_id}><strong>{tagText(d)}</strong><span>{`${name(d)} · ${d.laptop_tag || 'a laptop'}`}</span></li>)}
          </ul>
          {kept > 0 && (
            <p className="as-quiet">
              {`${kept} of the ticked devices ${kept === 1 ? 'has' : 'have'} history of ${kept === 1 ? 'its' : 'their'} own (a note, a ticket, a document, a change by hand) and cannot be deleted here; retire ${kept === 1 ? 'it' : 'them'} from ${kept === 1 ? 'its' : 'their'} page instead.`}
            </p>
          )}
          <p className="as-quiet">Written with your name and the time on each row.</p>
        </>
      )
    });
    if (ok) await run(personal ? 'personal' : 'delete', ids);
  };

  /*
   * Send back to the inbox: out of the register, back in Discovery as new,
   * to be decided with a click - and never linked by itself again. For what
   * an older agent linked before today's rules.
   */
  const askInbox = async () => {
    const ids = sendable.map((d) => d.asset_id);
    const held = chosen.length - sendable.length;
    const ok = await confirm({
      title: `Send ${ids.length} device${ids.length === 1 ? '' : 's'} back to the inbox?`,
      confirmLabel: 'Send back', danger: true,
      message: (
        <>
          <p>
            {`${ids.length === 1 ? 'It leaves' : 'They leave'} the register, with the history ${ids.length === 1 ? 'its' : 'their'} linking and housekeeping wrote (moves, a pin on the map), and ${ids.length === 1 ? 'returns' : 'return'} to the Discovery inbox as new. There you decide each with a click: Accept if it is the firm’s, Ignore if it does not belong in the register, Personal if it is somebody’s own. Nothing sent back is linked by itself again.`}
          </p>
          <ul className="as-tree" data-testid="inbox-changes">
            {sendable.map((d) => <li key={d.asset_id}><strong>{tagText(d)}</strong><span>{`${name(d)} · ${d.laptop_tag || 'a laptop'}`}</span></li>)}
          </ul>
          {held > 0 && (
            <p className="as-quiet">{`${held} of the ticked devices ${held === 1 ? 'has' : 'have'} a ticket or a document and ${held === 1 ? 'stays' : 'stay'}; retire ${held === 1 ? 'it' : 'them'} from ${held === 1 ? 'its' : 'their'} page instead.`}</p>
          )}
        </>
      )
    });
    if (ok) await run('inbox', ids);
  };

  if (loadError && !data) {
    return (
      <div className="container">
        <p><BackLink fallback={{ label: 'Discovery', path: '/assets/discovery' }} /></p>
        <p className="as-note" data-tone="warning" role="alert">{loadError}</p>
      </div>
    );
  }

  return (
    <div className="container">
      <p><BackLink fallback={{ label: 'Discovery', path: '/assets/discovery' }} /></p>
      <div className="page-header">
        <h1 className="page-title">Linked automatically</h1>
        <p className="page-description">
          Devices the register made from a laptop’s report without anybody clicking, newest first. Tick the
          ones that are not the firm’s - a home printer, a laptop’s own camera - and delete them; mark the personal
          ones, which become the person’s own items without a tag; keep the rest, and each gets its asset tag.
          Or send them back to the Discovery inbox to decide each again with a click.
        </p>
      </div>

      <div className="as-toolbar as-cleanup-filters">
        <select value={since} aria-label="Linked when" onChange={(e) => setSince(e.target.value)}>
          {SINCE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={kind} aria-label="Kind" onChange={(e) => setKind(e.target.value)}>
          <option value="">Every kind</option>
          {Object.entries(kinds).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
        <select value={bus} aria-label="Bus" onChange={(e) => setBus(e.target.value)}>
          <option value="">Every bus</option>
          {Object.entries(BUSES).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
        <select value={where} aria-label="Seen where" onChange={(e) => setWhere(e.target.value)}>
          <option value="">Seen anywhere</option>
          {Object.entries(WHERE).map(([k, [label]]) => <option key={k} value={k}>{label}</option>)}
        </select>
        <select value={holder} aria-label="Whose" onChange={(e) => setHolder(e.target.value)}>
          <option value="">Everybody’s</option>
          <option value="me">Mine</option>
        </select>
        <label className="as-check-inline">
          <input type="checkbox" checked={rulesOnly} onChange={(e) => setRulesOnly(e.target.checked)} />
          {' Only what today’s rules would not add'}
        </label>
        <span className="as-spacer" />
        {data && <span className="as-quiet">{`${devices.length} device${devices.length === 1 ? '' : 's'}`}</span>}
      </div>

      {loadError && <p className="as-error" role="alert">{loadError}</p>}
      {error && <p className="as-error" role="alert">{error}</p>}
      {note && <p className="as-note" data-tone="info" role="status">{note}</p>}

      {picked.size > 0 && (
        <div className="as-bulk" data-testid="bulk-cleanup">
          <strong>{`${picked.size} ticked`}</strong>
          <span className="as-quiet">
            {deletable.length === chosen.length
              ? 'None has history of its own.'
              : `${chosen.length - deletable.length} ${chosen.length - deletable.length === 1 ? 'has' : 'have'} history and cannot be deleted here.`}
          </span>
          <button type="button" className="btn btn-danger btn-small" disabled={busy || deletable.length === 0} onClick={() => askDelete(false)}>
            Delete (junk)
          </button>
          <button type="button" className="btn btn-secondary btn-small" disabled={busy || chosen.length === 0} onClick={() => askDelete(true)}>
            {holders.length === 1 ? `Mark personal (never link again for ${holders[0]})` : 'Mark personal (never link again for its holder)'}
          </button>
          <button type="button" className="btn btn-secondary btn-small" disabled={busy || sendable.length === 0} onClick={askInbox}>
            Send back to the inbox
          </button>
          <button type="button" className="btn btn-plain btn-small" disabled={busy} onClick={() => run('keep', chosen.map((d) => d.asset_id))}>
            Keep (gives it its tag)
          </button>
          <span className="as-spacer" />
          <button type="button" className="btn btn-plain btn-small" onClick={() => setPicked(new Set())}>Untick all</button>
        </div>
      )}

      {data && devices.length === 0 ? (
        <div className="empty-state"><p>Nothing linked automatically in this period that has not been looked at.</p></div>
      ) : (
        <div className="as-table-wrap">
          <table className="as-table as-cleanup">
            <thead>
              <tr>
                <th aria-label="Choose">
                  <input type="checkbox" className="as-select" aria-label="Choose every device"
                    checked={devices.length > 0 && devices.every((d) => picked.has(d.asset_id))}
                    onChange={(e) => setPicked(e.target.checked ? new Set(devices.map((d) => d.asset_id)) : new Set())} />
                </th>
                <th>Device</th><th>Laptop</th><th>Holder</th><th>Kind</th><th>Seen</th><th>Linked</th><th>History</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((d) => {
                const [word, tone] = WHERE[d.seen_where] || WHERE.unknown;
                return (
                  <tr key={d.asset_id} data-id={d.asset_id} data-history={String(d.has_history)}>
                    <td>
                      <input type="checkbox" className="as-select" checked={picked.has(d.asset_id)} onChange={() => toggle(d.asset_id)}
                        aria-label={`Choose ${tagText(d)}`} />
                    </td>
                    <td data-label="Device">
                      <Link to={`/assets/${d.asset_id}`} state={cameFrom('Linked automatically')} className="as-tag">{tagText(d)}</Link>
                      <span className="as-sub">{[name(d), d.serial_number && `serial ${d.serial_number}`].filter(Boolean).join(' · ')}</span>
                    </td>
                    <td data-label="Laptop">
                      {d.laptop_id ? <Link to={`/assets/${d.laptop_id}`} state={cameFrom('Linked automatically')}>{d.laptop_tag}</Link> : <span className="as-quiet">—</span>}
                      {d.laptop_id && <span className="as-sub">{[d.laptop_make, d.laptop_model].filter(Boolean).join(' ')}{d.still_plugged_in ? '' : ' · no longer plugged in'}</span>}
                    </td>
                    <td data-label="Holder">{d.holder_name || <span className="as-quiet">Nobody</span>}</td>
                    <td data-label="Kind">
                      <span className="as-chip" data-tone="info">{kinds[d.kind] || d.kind}</span>
                      {d.bus && <span className="as-sub">{BUSES[d.bus] || d.bus}</span>}
                      {d.rules_say && (
                        <span className="as-chip" data-tone="warning" data-testid="rules-say"
                          title="Linked by an older agent; today’s rules would not add it">{`Not added today: ${d.rules_say}`}</span>
                      )}
                    </td>
                    <td data-label="Seen"><span className="as-chip" data-tone={tone}>{word}</span></td>
                    <td data-label="Linked">{when(d.created_at)}</td>
                    <td data-label="History">
                      {d.has_history
                        ? <span className="as-chip" data-tone="warning" title="Has a note, a ticket, a document or a change by hand; cannot be deleted here">Has history</span>
                        : <span className="as-quiet">None</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {confirmDialog}
    </div>
  );
}
